import Vue from 'vue'
import forIn from 'lodash/forIn'
import isEmpty from 'lodash/isEmpty'
import Platform from '@/plugin-platforms'
import moment from 'moment'
import semver from 'semver'
import uuidv4 from 'uuid/v4'

const saveKey = 'icondrop'

const saveSettings = (state) => {
  // Save to Storage
  if (state.loaded) {
    // Log it into Amplitude
    const amplitude = Vue.prototype.$amplitude
    var identify = new amplitude.Identify()
      // Settings
      .set('settings', {
        ...state,
        token: null
      })
    amplitude.getInstance().identify(identify)

    Platform.storage.putJSON(saveKey, state)
  } else {
    console.log('Saving settings before loading... Ignored.')
  }
}

export const state = () => ({
  lastVersion: '2.0.0',
  deviceId: null,
  importDND: {
    free: {
      icon: false,
      illustration: false,
      photo: false
    },
    premium: {
      icon: false,
      illustration: false,
      photo: false
    }
  },
  premiumOption: 'subscription',
  loaded: false,
  token: {},
  installedAt: null,
  asset: 'icon',
  iconFormat: 'png',
  illustrationFormat: 'png',
  photoFormat: 'thumb',
  imageSize: 'thumb',
  fillType: 'fill',
  previewSize: 'md',
  theme: 'light',
  platformInfo: {},
  autoSave: {
    enabled: false,
    path: '/'
  },
  offersClosed: [],
  // For App
  autolaunch: false
})

export const getters = {
  tokenInfo (state) {
    return state.token
  },
  showLogin (state) {
    return isEmpty(state.token) && moment.unix(state.installedAt).diff(moment(), 'days') > 7
  },
  showOnboarding (state) {
    // Check if we need to show Updates
    const lastVersion = semver.coerce(state.lastVersion)
    const currentVersion = semver.coerce(process.env.VUE_APP_VERSION)
    // There's no proper version available
    // if we're running it in beta
    if (currentVersion) {
      return semver.lt(lastVersion, currentVersion)
    } else {
      return false
    }
  }
}

export const mutations = {
  load (state) {
    let data = Platform.storage.getJSON(saveKey)

    if (data) {
      forIn(data, (value, key) => {
        state[key] = value
      })
    }

    // Get Platform Info for detailed analysis
    state.platformInfo = Platform.info

    console.log('State Loaded:', data)
    state.loaded = true
  },
  setPreviewSize (state, size) {
    state.previewSize = size

    Vue.prototype.$amplitude.getInstance().logEvent('settings', {
      previewSize: size
    })

    saveSettings(state)
  },
  setIconFormat (state, format) {
    state.iconFormat = format

    Vue.prototype.$amplitude.getInstance().logEvent('settings', {
      iconFormat: format
    })

    saveSettings(state)
  },
  setIllustrationFormat (state, format) {
    state.illustrationFormat = format

    Vue.prototype.$amplitude.getInstance().logEvent('settings', {
      illustrationFormat: format
    })

    saveSettings(state)
  },
  setPhotoFormat (state, format) {
    state.photoFormat = format

    Vue.prototype.$amplitude.getInstance().logEvent('settings', {
      photoFormat: format
    })

    saveSettings(state)
  },
  setAsset (state, asset) {
    state.asset = asset

    Vue.prototype.$amplitude.getInstance().logEvent('settings', {
      asset
    })

    saveSettings(state)
  },
  setImageSize (state, size) {
    state.imageSize = size

    Vue.prototype.$amplitude.getInstance().logEvent('settings', {
      imageSize: size
    })

    saveSettings(state)
  },
  setFillType (state, size) {
    state.fillType = size

    Vue.prototype.$amplitude.getInstance().logEvent('settings', {
      fillType: size
    })

    saveSettings(state)
  },
  setImportDND (state, { asset, price, value }) {
    state.importDND[price][asset] = value

    Vue.prototype.$amplitude.getInstance().logEvent('settings', {
      importDND: { asset, price, value }
    })

    saveSettings(state)
  },
  setPremiumOption (state, value) {
    state.premiumOption = value

    Vue.prototype.$amplitude.getInstance().logEvent('settings', {
      premiumOption: value
    })

    saveSettings(state)
  },
  setTheme (state, theme) {
    state.theme = theme

    Vue.prototype.$amplitude.getInstance().logEvent('settings', {
      theme
    })

    let root = document.getElementsByTagName('html')[0]
    root.className = theme
    // root.className += ' myCssClass'
    saveSettings(state)
  },
  setToken (state, token) {
    // eslint-disable-next-line indent
//////////////////////////////////////////////////
////////////////
/////////////////////////
////////////
//////////////////////
/////
//////////////////////////////////////////////
//////////////////////////////////////
/////////
    if (token) {
      state.token = token
      // Set in localStorage for Myscout Communication
      window.localStorage.setItem('apollo-token', token.access_token)
    } else {
      state.token = {}
      // Reset in localStorage for Myscout Communication
      window.localStorage.removeItem('apollo-token')
    }
    // eslint-disable-next-line indent
//////////
    saveSettings(state)
  },
  setInstalledAt (state) {
    // Set Install Time of Plugin, if it's null
    if (!state.installedAt) {
      state.installedAt = moment().unix()
      state.deviceId = uuidv4()
    }
  },
  setClosedOffer (state, value) {
    state.offersClosed.push(value)
    saveSettings(state)
  },
  setLastVersion (state) {
    state.lastVersion = process.env.VUE_APP_VERSION
    saveSettings(state)
  },
  setAutoLaunch (state, value) {
    state.autolaunch = value

    Platform.setAutoLaunch(value)

    saveSettings(state)
  }
}

export const actions = {
  async load ({ state, commit, rootGetters, dispatch }) {
    commit('load')
    commit('setTheme', state.theme)
    commit('setInstalledAt')

    const hasUpdatesInfo = process.env.VUE_APP_ONBOARDING || false

    if (!hasUpdatesInfo) {
      commit('setLastVersion')
    }

    // Set User Login
    if (!isEmpty(state.token)) {
      await dispatch('user/fetch', null, { root: true })
    }

    const amplitude = Vue.prototype.$amplitude

    const user = rootGetters['user/get']
    if (user) {
      dispatch('user/getSelectedDesigners', null, { root: true })
      // Log to Analytics
      amplitude.setUserId(user.id)
    } else {
      amplitude.setUserId(null)
    }

    // Set Device ID
    amplitude.setDeviceId(state.deviceId)

    // Set User Tools
    var identify = new amplitude.Identify()
      .append('plugins', Platform.platform)
      // Last Plugin Used
      .set('last_plugin', Platform.platform)
      // Version
      .set('version', process.env.VUE_APP_VERSION)
    amplitude.getInstance().identify(identify)

    Vue.prototype.$amplitude.getInstance().logEvent('identify', {
      'plugin': Platform.platform,
      'version': process.env.VUE_APP_VERSION
    })
  }
}
